import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { Draft, ProductTransferData, useCompany, useProduct } from 'zens-store'
import { Spacing, Text } from 'zens-storybook'
import { InfoBlock, Form, CompanySelect } from '@dms/component'
import { number, object } from 'yup'

const styles = StyleSheet.create({
  container: {},
  block: {
    marginBottom: Spacing.M,
  },
  current: {
    margin: Spacing.S,
  },
})

interface Props extends ViewProps {
  companyId: number
  productId: number
  onTransfer?: (draft: Draft<ProductTransferData>) => Promise<void>
}
const ProductTransferInfo = (props: Props) => {
  const { companyId, productId, onTransfer, style, ...otherProps } = props

  const company = useCompany({ companyId })
  const product = useProduct({ companyId, productId })

  const draft = useMemo(() => new Draft({ id: NaN, company_id: companyId, ...product.data?.data }), [product.data])

  const { t } = useTranslation()

  const config = {
    groups: [
      {
        label: t('Transfer details'),
        fields: [
          {
            key: 'owner_company_id',
            label: t('Current company'),
            FieldInputComponent: () => <Text style={styles.current}>{company.data?.name}</Text>,
          },
          {
            key: 'transfer_company_id',
            label: t('Transfer device to'),
            help: t(
              'By picking another company the device will be transferred to the chosen company. The device will not be visible within the current company anymore.',
            ),
            FieldInputComponent: CompanySelect,
            fieldProps: {
              filter: (id: number) => companyId !== id,
            },
          },
        ],
      },
    ],
  }

  const schema = object({
    transfer_company_id: number().required(t('Company is required')),
  })

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <Form
        draft={draft}
        config={config}
        schema={schema}
        buttons={[
          [
            {
              title: t('Transfer device'),
              onPress: onTransfer,
              validate: true,
            },
            {
              title: t('Cancel'),
              href: `/companies/${companyId}/products/${productId}`,
              light: true,
            },
          ],
        ]}
      />
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <InfoBlock.Skeleton style={styles.block} title={t('Transfer details')} />
    </View>
  )
}
ProductTransferInfo.Skeleton = Skeleton

export default observer(ProductTransferInfo)
